// frontend/src/components/UserManagement/UserManagement.js

import React, { useState, useEffect, useContext } from 'react';
import apiClient from '../../apiClient';
import { UserContext } from '../../UserContext';
import './UserManagement.css';

function UserManagement() {
  const { user: currentUser } = useContext(UserContext);
  // Global admin ser alla kliniker och kan dela ut global admin.
  // Klinikadmin hanterar endast sin egen klinik.
  const isGlobalAdmin = currentUser?.is_admin;

  const [users, setUsers] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [templates, setTemplates] = useState([]); // <-- NYTT
  const [form, setForm] = useState({
    username: '',
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    personal_number: '',
    clinic_id: '',
    is_admin: false,
    is_clinic_admin: false,
    default_journaltemplate_id: '', // <-- NYTT
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editUserId, setEditUserId] = useState(null);

  useEffect(() => {
    fetchUsers();
    if (isGlobalAdmin) {
      fetchClinics();
    }
    fetchTemplates(); // <-- NYTT
  }, [isGlobalAdmin]);

  const fetchUsers = async () => {
    try {
      const response = await apiClient.get('/api/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchClinics = async () => {
    try {
      const response = await apiClient.get('/api/clinics');
      setClinics(response.data);
    } catch (error) {
      console.error('Error fetching clinics:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await apiClient.get('/api/journalmallar');
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Klinikadmin skapar alltid användare i sin egen klinik
      const payload = { ...form };
      if (!isGlobalAdmin) {
        payload.clinic_id = currentUser?.clinic_id;
        delete payload.is_admin;
      }

      if (isEditing) {
        if (!payload.password) {
          delete payload.password;
        }
        await apiClient.put(`/api/users/${editUserId}`, payload);
        alert('Användare uppdaterad');
      } else {
        await apiClient.post('/api/users', payload);
        alert('Användare skapad');
      }

      setForm({
        username: '',
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        personal_number: '',
        clinic_id: '',
        is_admin: false,
        is_clinic_admin: false,
        default_journaltemplate_id: '', // <-- NYTT
      });
      setIsEditing(false);
      fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      alert('Ett fel uppstod');
    }
  };

  const handleEdit = (user) => {
    setForm({
      username: user.username || '',
      email: user.email || '',
      password: '',
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      personal_number: user.personal_number || '',
      clinic_id: user.clinic_id || '',
      is_admin: user.is_admin || false,
      is_clinic_admin: user.is_clinic_admin || false,
      default_journaltemplate_id: user.default_journaltemplate_id || '', // <-- NYTT
    });
    setIsEditing(true);
    setEditUserId(user.user_id);
  };

  const handleDelete = async (id) => {
    try {
      // Första anropet - kolla vad som finns
      const response = await apiClient.delete(`/api/users/${id}`);
      const data = response.data;

      if (data.requires_confirmation) {
        // Bygg upp bekräftelsemeddelande
        const records = data.deletable_records;
        let message = `Användare: ${data.user_name}\n\nFöljande personliga poster kommer att tas bort:\n`;

        if (records.inspelningar > 0) message += `• ${records.inspelningar} inspelningar/sessioner\n`;
        if (records.moten > 0) message += `• ${records.moten} möten\n`;
        if (records.snabbfraser > 0) message += `• ${records.snabbfraser} snabbfraser\n`;

        message += '\nÄr du säker på att du vill ta bort användaren och alla dessa poster?';

        if (window.confirm(message)) {
          // Bekräftat - ta bort med force
          await apiClient.delete(`/api/users/${id}?force=true`);
          alert('Användare och tillhörande poster borttagna');
          fetchUsers();
        }
      } else {
        // Inga poster att bekräfta, användaren togs bort direkt
        alert('Användare borttagen');
        fetchUsers();
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert(error.response?.data?.msg || 'Ett fel uppstod vid borttagning');
    }
  };

  const handleFreeze = async (user) => {
    const action = user.is_active ? 'frysa' : 'återaktivera';
    if (!window.confirm(`Vill du ${action} kontot för ${user.first_name} ${user.last_name}?`)) {
      return;
    }

    try {
      const endpoint = user.is_active ? 'freeze' : 'unfreeze';
      await apiClient.post(`/api/users/${user.user_id}/${endpoint}`);
      alert(user.is_active ? 'Kontot har frysets' : 'Kontot har återaktiverats');
      fetchUsers();
    } catch (error) {
      console.error('Error freezing/unfreezing user:', error);
      alert(error.response?.data?.msg || 'Ett fel uppstod');
    }
  };

  return (
    <div className="user-management-container">
      <h2>Användarhantering</h2>
      <form onSubmit={handleSubmit} className="user-form">
        <input
          type="text"
          name="first_name"
          placeholder="Förnamn"
          value={form.first_name}
          onChange={handleFormChange}
          required
        />
        <input
          type="text"
          name="last_name"
          placeholder="Efternamn"
          value={form.last_name}
          onChange={handleFormChange}
          required
        />
        <input
          type="text"
          name="personal_number"
          placeholder="Personnummer"
          value={form.personal_number}
          onChange={handleFormChange}
          required
        />
        <input
          type="text"
          name="username"
          placeholder="Användarnamn"
          value={form.username}
          onChange={handleFormChange}
          required
        />
        <input
          type="email"
          name="email"
          placeholder="E-post"
          value={form.email}
          onChange={handleFormChange}
          required
        />
        <input
          type="password"
          name="password"
          placeholder="Lösenord"
          value={form.password}
          onChange={handleFormChange}
          required={!isEditing}
        />

        {/* Klinikval - endast global admin. Klinikadmin skapar alltid i egen klinik. */}
        {isGlobalAdmin && (
          <select
            name="clinic_id"
            value={form.clinic_id}
            onChange={handleFormChange}
            required
          >
            <option value="">-- Välj Klinik --</option>
            {clinics.map((clinic) => (
              <option key={clinic.clinic_id} value={clinic.clinic_id}>
                {clinic.clinic_name}
              </option>
            ))}
          </select>
        )}

        <select
          name="default_journaltemplate_id"
          value={form.default_journaltemplate_id}
          onChange={handleFormChange}
        >
          <option value="">-- Välj standardjournalmall --</option>
          {templates.map((t) => (
            <option key={t.template_id} value={t.template_id}>
              {t.template_name}
            </option>
          ))}
        </select>

        {/* Admin-roller (global + klinikadmin) delas endast ut av global admin.
            En klinikadmin hanterar bara vanliga användare. */}
        {isGlobalAdmin && (
          <>
            <label style={{ marginLeft: '10px' }}>
              Admin:
              <input
                type="checkbox"
                name="is_admin"
                checked={form.is_admin}
                onChange={handleFormChange}
                style={{ marginLeft: '5px' }}
              />
            </label>

            <label style={{ marginLeft: '10px' }}>
              Klinikadmin:
              <input
                type="checkbox"
                name="is_clinic_admin"
                checked={form.is_clinic_admin}
                onChange={handleFormChange}
                style={{ marginLeft: '5px' }}
              />
            </label>
          </>
        )}

        <button type="submit">
          {isEditing ? 'Uppdatera' : 'Skapa'}
        </button>
      </form>

      <table className="user-table">
        <thead>
          <tr>
            <th>Förnamn</th>
            <th>Efternamn</th>
            <th>Personnummer</th>
            <th>Användarnamn</th>
            <th>E-post</th>
            <th>Klinik-ID</th>
            <th>Admin</th>
            <th>Klinikadmin</th>
            <th>Status</th>
            <th>Åtgärder</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.user_id} className={!u.is_active ? 'user-frozen' : ''}>
              <td>{u.first_name}</td>
              <td>{u.last_name}</td>
              <td>{u.personal_number}</td>
              <td>{u.username}</td>
              <td>{u.email}</td>
              <td>{u.clinic_id}</td>
              <td>{u.is_admin ? 'Ja' : 'Nej'}</td>
              <td>{u.is_clinic_admin ? 'Ja' : 'Nej'}</td>
              <td>
                <span className={`status-badge ${u.is_active ? 'status-active' : 'status-frozen'}`}>
                  {u.is_active ? 'Aktiv' : 'Fryst'}
                </span>
              </td>
              <td>
                {/* Klinikadmin får bara hantera vanliga användare, inte andra admins */}
                {(isGlobalAdmin || (!u.is_admin && !u.is_clinic_admin)) ? (
                  <>
                    <button onClick={() => handleEdit(u)}>Redigera</button>
                    <button
                      onClick={() => handleFreeze(u)}
                      className={u.is_active ? 'btn-freeze' : 'btn-unfreeze'}
                    >
                      {u.is_active ? 'Frys' : 'Aktivera'}
                    </button>
                    <button onClick={() => handleDelete(u.user_id)} className="btn-delete">Ta Bort</button>
                  </>
                ) : (
                  <span className="user-managed-elsewhere">Hanteras av administratör</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default UserManagement;
